/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import logo from "./assets/images/logo.png";
import fabriceAI from "./assets/images/fabrice-ai-logo.svg";
import newChat from "./assets/images/new-chat.svg";
import toggleNavbar from "./assets/images/sidebar-toggle.svg";
import packageJson from "../package.json";
import mic from "./assets/images/mic.svg";
import send from "./assets/images/send.svg";
import sendDisabled from "./assets/images/send-disabled.svg";
import scrollDown from "./assets/images/scroll-down.svg";
import userChat from "./assets/images/user-chat.svg";
import aiChat from "./assets/images/ai-chat.svg";
import audioWave from "./assets/images/audio-wave.gif";
import { usePersistedState } from "./app/hooks";
import {
  categorizeAndGroupData,
  extractLastCompleteObject,
  parseMetadata,
  separateObjects,
} from "./utils/Utils";
import Markdown from "react-markdown";
import { LazyLoadImage } from "react-lazy-load-image-component";
import DeviceUUID from "device-uuid";
import {
  AvatarState,
  AvatarStatus,
  ChatData,
  ChatMode,
  Language,
  RequestBody,
} from "./types/Types";
import { ChatModeSwitch } from "./components/ChatModeSwitch";
import { useVoiceVisualizer, VoiceVisualizer } from "react-voice-visualizer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AvatarComponent,
  AvatarComponentRef,
} from "./components/AvatarComponent";
import useCurrentHeight from "./hooks/useCurrentHeight";
import { languages, LanguageSelection } from "./components/LanguageSelection";
import listening from "./assets/images/listening.gif";
import processing from "./assets/images/processing.gif";
import speaking from "./assets/images/speaking.gif";
import { faro } from "@grafana/faro-react";
import { getWaveBlob } from "./components/wavBlob";
import checkCircle from "./assets/images/checkCircle.svg";
import closeCircle from "./assets/images/closeCircle.svg";
import DelphiAudioIframe from "./components/DelphiAudioIframe";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import PitchFabriceTextView from "./components/PitchFabriceTextView";
import PitchFabriceVoiceView from "./components/PitchFabriceVoiceView";

export function loadDelphiAudioInteraction() {
  return <DelphiAudioIframe />;
}

interface AudioConversationProps {
  fetchDataStream: (requestBody: RequestBody) => Promise<string>;
}

const AudioConversation: React.FC<AudioConversationProps> = ({
  fetchDataStream,
}) => {
  const [loading, setLoading] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [interactionState, setInteractionState] = useState<
    "listening" | "thinking" | "speaking" | "idle"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const filterNodeRef = useRef<BiquadFilterNode | null>(null);
  const audioBufferRef = useRef<Float32Array[]>([]);
  const isSpeakingRef = useRef<boolean>(false);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAudioTimeRef = useRef<number>(Date.now());
  const speechStartTimeRef = useRef<number | null>(null);
  const isInitializingRef = useRef<boolean>(false);
  const retryCountRef = useRef<number>(0);
  const MAX_RETRIES = 3;
  const SILENCE_THRESHOLD = 1000;
  const MIN_SPEECH_DURATION = 750;
  const VAD_THRESHOLD = 0.03;
  const SILENCE_VAD_THRESHOLD = 0.004;
  const SAMPLE_RATE = 16000;
  const MIN_BLOB_SIZE = 1000;

  const checkMicPermissions = async (): Promise<boolean> => {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      if (permissionStatus.state === "prompt") {
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          return true;
        } catch (err) {
          console.error("Microphone permission prompt rejected:", err);
          return false;
        }
      }
      return permissionStatus.state === "granted";
    } catch (error) {
      console.error("Error checking microphone permissions:", error);
      return false;
    }
  };

  useEffect(() => {
    if (isCallActive && !isInitializingRef.current) {
      setInteractionState("listening");
      startContinuousListening();
    }
    return () => {
      if (!isCallActive) {
        stopContinuousListening();
      }
    };
  }, [isCallActive]);

  const startContinuousListening = async () => {
    if (isInitializingRef.current) {
      console.log("Already initializing audio processing");
      return;
    }

    if (retryCountRef.current >= MAX_RETRIES) {
      setErrorMessage(
        "Unable to access microphone after multiple attempts. Please check settings."
      );
      setInteractionState("idle");
      isInitializingRef.current = false;
      return;
    }

    isInitializingRef.current = true;
    retryCountRef.current += 1;
    try {
      const hasPermission = await checkMicPermissions();
      if (!hasPermission) {
        setErrorMessage(
          "Microphone access denied. Please allow microphone access in browser settings."
        );
        setInteractionState("idle");
        setTimeout(() => {
          isInitializingRef.current = false;
          if (isCallActive) startContinuousListening();
        }, 5000);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: SAMPLE_RATE,
        },
        video: false,
      });
      streamRef.current = stream;

      audioContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
      sourceNodeRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      filterNodeRef.current = audioContextRef.current.createBiquadFilter();
      filterNodeRef.current.type = "highpass";
      filterNodeRef.current.frequency.setValueAtTime(
        100,
        audioContextRef.current.currentTime
      );
      processorNodeRef.current = audioContextRef.current.createScriptProcessor(
        4096,
        1,
        1
      );

      processorNodeRef.current.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);
        let maxAmplitude = 0;
        for (let i = 0; i < inputData.length; i++) {
          const absValue = Math.abs(inputData[i]);
          if (absValue > maxAmplitude) {
            maxAmplitude = absValue;
          }
        }
        const currentTime = Date.now();

        if (maxAmplitude > VAD_THRESHOLD) {
          console.log(`Speech detected with amplitude: ${maxAmplitude}`);
          if (!isSpeakingRef.current) {
            isSpeakingRef.current = true;
            if (audioBufferRef.current.length === 0) {
              speechStartTimeRef.current = currentTime;
            }
          }
          audioBufferRef.current.push(new Float32Array(inputData));
          lastAudioTimeRef.current = currentTime;
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
          }
          silenceTimeoutRef.current = setTimeout(() => {
            if (isSpeakingRef.current) {
              console.log("Silence detected, processing segment");
              processSpeechSegment();
            }
          }, SILENCE_THRESHOLD);
        } else if (
          isSpeakingRef.current &&
          maxAmplitude < SILENCE_VAD_THRESHOLD &&
          currentTime - lastAudioTimeRef.current >= SILENCE_THRESHOLD
        ) {
          console.log("Silence detected during speech, processing segment");
          processSpeechSegment();
        }
      };

      sourceNodeRef.current.connect(filterNodeRef.current);
      filterNodeRef.current.connect(processorNodeRef.current);
      processorNodeRef.current.connect(audioContextRef.current.destination);

      setInteractionState("listening");
      setErrorMessage(null);
      isInitializingRef.current = false;
      retryCountRef.current = 0;
    } catch (error) {
      console.error("Error starting listening:", error);
      let errorMsg =
        "Failed to access microphone. Please ensure a microphone is connected.";
      if (error instanceof DOMException) {
        errorMsg =
          error.name === "NotAllowedError"
            ? "Microphone access denied. Please allow microphone access in browser settings."
            : "No microphone found or access blocked.";
      }
      setErrorMessage(errorMsg);
      setInteractionState("idle");
      setTimeout(() => {
        isInitializingRef.current = false;
        if (isCallActive) startContinuousListening();
      }, 5000);
    }
  };

  const stopContinuousListening = () => {
    if (processorNodeRef.current) processorNodeRef.current.disconnect();
    if (filterNodeRef.current) filterNodeRef.current.disconnect();
    if (sourceNodeRef.current) sourceNodeRef.current.disconnect();
    if (audioContextRef.current) audioContextRef.current.close();
    if (streamRef.current)
      streamRef.current.getTracks().forEach((track) => track.stop());
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);

    audioBufferRef.current = [];
    isSpeakingRef.current = false;
    speechStartTimeRef.current = null;
    setInteractionState("idle");
    console.log("Stopped listening");
  };

  const processSpeechSegment = async () => {
    if (!isSpeakingRef.current || audioBufferRef.current.length === 0) {
      setInteractionState("listening");
      return;
    }

    const currentTime = Date.now();
    const speechDuration =
      speechStartTimeRef.current !== null
        ? currentTime - speechStartTimeRef.current
        : 0;
    if (speechDuration < MIN_SPEECH_DURATION) {
      console.log(`Speech too short: ${speechDuration}ms`);
      audioBufferRef.current = [];
      isSpeakingRef.current = false;
      speechStartTimeRef.current = null;
      setInteractionState("listening");
      return;
    }

    isSpeakingRef.current = false;
    speechStartTimeRef.current = null;
    const audioBlob = createWavBlob(audioBufferRef.current);
    audioBufferRef.current = [];

    if (audioBlob.size < MIN_BLOB_SIZE) {
      console.log(`Blob too small: ${audioBlob.size} bytes`);
      setInteractionState("listening");
      return;
    }

    if (audioBlob.size > 0 && isCallActive) {
      await transcribeAndProcess(audioBlob);
    } else {
      setInteractionState("listening");
    }
  };

  const createWavBlob = (buffers: Float32Array[]): Blob => {
    const numChannels = 1;
    const samples = buffers.reduce((acc: number[], buf) => {
      return acc.concat(Array.from(buf));
    }, []);
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, SAMPLE_RATE, true);
    view.setUint32(28, SAMPLE_RATE * numChannels * 2, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, "data");
    view.setUint32(40, samples.length * 2, true);

    for (let i = 0; i < samples.length; i++) {
      const sample = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(
        44 + i * 2,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true
      );
    }

    return new Blob([buffer], { type: "audio/wav" });
  };

  const writeString = (view: DataView, offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  const transcribeAndProcess = async (audioBlob: Blob) => {
    try {
      setInteractionState("thinking");
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.wav");
      formData.append("language", "en-US");
      formData.append("max_speakers", "1");
      formData.append("model_id", "scribe_v1");

      const response = await axios.post(
        "https://api.elevenlabs.io/v1/speech-to-text",
        formData,
        {
          headers: {
            "xi-api-key": process.env.REACT_APP_ELEVENLABS_API_KEY || "",
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("ElevenLabs API response:", JSON.stringify(response.data));
      const transcribedText = response.data.text?.trim();
      const languageCode = response.data.language_code;

      if (transcribedText && languageCode === "eng") {
        console.log(`Transcribed text: ${transcribedText}`);
        await processTextWithFabriceAI(transcribedText);
      } else {
        console.warn(
          `Invalid transcription detected: text="${transcribedText}", language_code="${languageCode}"`
        );
        setErrorMessage(
          languageCode !== "eng"
            ? "Only English speech is supported. Please speak in English."
            : "Transcription invalid or silence detected."
        );
        setInteractionState(isCallActive ? "listening" : "idle");
      }
    } catch (error: any) {
      console.error(
        "Transcription error:",
        error.response?.data || error.message
      );
      setErrorMessage("Failed to transcribe audio. Check API key or network.");
      setInteractionState(isCallActive ? "listening" : "idle");
    }
  };

  const processTextWithFabriceAI = async (text: string) => {
    try {
      setInteractionState("thinking");
      const deviceId = uuidv4();
      const requestBody: RequestBody = {
        query: text,
        deviceId,
        tokenId: `${deviceId}-${Date.now()}`,
      };

      const rawResponse = await fetchDataStream(requestBody);
      const responseText =
        rawResponse
          .match(/{"response":\s*"(.*?)"}/g)
          ?.map((match: string) => JSON.parse(match).response)
          .join(" ") || "";

      if (responseText.trim()) {
        console.log(`Fabrice AI response: ${responseText}`);
        setInteractionState(isCallActive ? "listening" : "idle");
      } else {
        console.warn("No response from Fabrice AI.");
        setErrorMessage("No response from Fabrice AI.");
        setInteractionState(isCallActive ? "listening" : "idle");
      }
    } catch (error) {
      console.error("Fabrice AI error:", error);
      setErrorMessage("Failed to process response from Fabrice AI.");
      setInteractionState(isCallActive ? "listening" : "idle");
    }
  };

  const endCall = () => {
    stopContinuousListening();
    setIsCallActive(false);
    setInteractionState("idle");
  };

  return (
    <div className="audio-conversation flex flex-col items-center">
      {isCallActive ? (
        <div className="flex flex-col items-center mt-4">
          <button
            onClick={endCall}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            End Call
          </button>
          {errorMessage && <p className="mt-2 text-red-600">{errorMessage}</p>}
        </div>
      ) : (
        <div className="flex flex-col items-center mt-4">
          <button
            onClick={() => setIsCallActive(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Start Call
          </button>
        </div>
      )}
    </div>
  );
};

// Rest of App.tsx remains unchanged
function App() {
  const questions = [
    "What is FJ Labs' purpose?",
    "What does FJ Labs' invest in?",
    "Why is Fabrice optimistic about climate change?",
    "What are the biggest mistakes marketplace founders make?",
    "How to make the most important decisions in your life?",
    "How to live asset light?",
    "What are fair Seed & Series A valuations?",
    "What is the key to happiness?",
  ];
  const deviceId = new DeviceUUID.DeviceUUID().get();
  const [openNavbar, setOpenNavbar] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const windowHeight = useCurrentHeight();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const dataStreamController = useRef<AbortController | null>(null);
  const metaDataController = useRef<AbortController | null>(null);
  const transcriptDataController = useRef<AbortController | null>(null);
  const defaultChat: ChatData = {
    selected: 1,
    chats: [
      {
        id: 1,
        createdAt: new Date().toISOString(),
        messages: [],
      },
    ],
  };
  const [chatData, setChatData] = usePersistedState("chatData", defaultChat);
  const selectedChat = chatData.chats.find(
    (chat) => chat.id === chatData.selected
  );
  const [chatMode, setChatMode] = useState<ChatMode>(ChatMode.DELPHI);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [dataStreamLoading, setDataStreamLoading] = useState(false);

  const recorderControls = useVoiceVisualizer();
  const {
    recordedBlob,
    error,
    startRecording,
    stopRecording,
    isRecordingInProgress,
    clearCanvas,
    recordingTime,
  } = recorderControls;
  const [transcripting, setTranscripting] = useState(false);
  const [greet, setGreet] = useState(true);
  const avatarComponentRef = useRef<AvatarComponentRef>(null);
  const [avatarState, setAvatarState] = useState<AvatarState>(AvatarState.IDLE);
  const [avatarStatus, setAvatarStatus] = useState<AvatarStatus>(
    AvatarStatus.NOT_INITIALIZED
  );
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    languages[0]
  );

  // Load Delphi script when chatMode is DELPHI
  useEffect(() => {
    if (chatMode === ChatMode.DELPHI) {
      console.log("Loading Delphi audio interaction...");
      loadDelphiAudioInteraction();

      // Cleanup function to remove scripts
      return () => {
        const scriptConfig = document.getElementById("delphi-page-script");
        const scriptLoader = document.getElementById("delphi-page-bootstrap");
        if (scriptConfig) document.body.removeChild(scriptConfig);
        if (scriptLoader) document.body.removeChild(scriptLoader);
        console.log("Cleaned up Delphi scripts");
      };
    }
  }, [chatMode]);

  const submitPrompt = async (prompt: string) => {
    scrollToBottom();
    setChatData((prevChatData) => {
      const newChatData: ChatData = JSON.parse(JSON.stringify(prevChatData));
      const selectedChat = newChatData.chats.find(
        (chat) => chat.id === newChatData.selected
      );
      if (selectedChat) {
        selectedChat.messages.push(
          {
            byUser: true,
            message: prompt,
            topSources: [],
            status: "complete",
          },
          {
            byUser: false,
            message: "",
            topSources: [],
            status: "incomplete",
          }
        );
      }

      return newChatData;
    });

    setDataStreamLoading(true);
    setLoadingMessage("");
    fetchApis(prompt);
  };

  const updateChatsWithStatus = (status: string) => {
    setChatData((prevChatData) => {
      const newChatData: ChatData = JSON.parse(JSON.stringify(prevChatData));
      const selectedChat = newChatData.chats.find(
        (chat) => chat.id === newChatData.selected
      );
      if (selectedChat) {
        const lastMessageIndex = selectedChat.messages.length - 1;
        if (lastMessageIndex >= 0) {
          selectedChat.messages[lastMessageIndex].status = status;
        }
      }
      return newChatData;
    });
  };

  const fetchApis = async (prompt: string) => {
    setLoading(true);
    setLoadingMessage("");
    const request = {
      query: prompt,
      deviceId: deviceId,
      tokenId: selectedChat?.tokenId ?? deviceId + "-" + Date.now(),
      threadId: selectedChat?.threadId,
    };
    try {
      await Promise.all([fetchDataStream(request), fetchMetaData(request)]);
    } catch (error: any) {
      console.log(error);
      if (error.name === "AbortError") {
        updateChatsWithStatus("abort");
        if (faro.api) {
          faro.api.pushError(error, {
            type: "network",
            context: {
              message: "Request aborted",
            },
          });
        } else {
          console.warn("Faro API not initialized, cannot push error:", error);
        }
      } else {
        updateChatsWithStatus("error");
        console.error("Fetch APIs error:", error);
      }
    } finally {
      setLoading(false);
      setDataStreamLoading(false);
      setChatData((prevChatData) => {
        const newChatData: ChatData = JSON.parse(JSON.stringify(prevChatData));
        const selectedChat = newChatData.chats.find(
          (chat) => chat.id === newChatData.selected
        );
        if (selectedChat) {
          const lastMessageIndex = selectedChat.messages.length - 1;
          if (
            lastMessageIndex >= 0 &&
            selectedChat.messages[lastMessageIndex].status === "incomplete"
          ) {
            selectedChat.messages[lastMessageIndex].status = "complete";
          }
        }
        return newChatData;
      });
    }
  };

  const fetchDataStream = async (requestBody: RequestBody) => {
    setDataStreamLoading(true);
    setAvatarState(AvatarState.PROCESSING);
    dataStreamController.current = new AbortController();
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/stream`, {
      signal: dataStreamController.current?.signal,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok || !response.body) return "";
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let fullResponse = "";
    while (true) {
      const { done, value } = await reader?.read();
      if (done || dataStreamController.current?.signal?.aborted) {
        break;
      }
      const chunk = decoder.decode(value, { stream: true });
      fullResponse += chunk;
      const objects = separateObjects(chunk);
      const threadId = objects[0].threadId;
      const message = objects.map((value) => value.response).join("");
      setLoadingMessage((prevState) => prevState + message);
      setChatData((prevChatData) => {
        const newChatData: ChatData = JSON.parse(JSON.stringify(prevChatData));
        const selectedChat = newChatData.chats.find(
          (chat) => chat.id === newChatData.selected
        );
        if (selectedChat) {
          if (threadId) selectedChat.threadId = threadId;
          if (!selectedChat.tokenId) selectedChat.tokenId = requestBody.tokenId;
          const lastMessageIndex = selectedChat.messages.length - 1;
          if (lastMessageIndex >= 0) {
            selectedChat.messages[lastMessageIndex].message += message;
          }
        }
        return newChatData;
      });
    }
    setDataStreamLoading(false);
    return fullResponse || "";
  };

  const fetchMetaData = async (requestBody: RequestBody) => {
    let buffer = "";
    metaDataController.current = new AbortController();
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/metadataStream`,
      {
        signal: metaDataController.current?.signal,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );
    if (!response.ok || !response.body) return;
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    let lastCompleteObjects: any[] | null = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done || metaDataController.current?.signal.aborted) break;
      const chunk = decoder.decode(value, { stream: true });
      buffer += separateObjects(chunk)
        .filter((v) => v.response !== "```" && v.response !== "json")
        .map((value) => value.response)
        .join("");
      const lastObjects = extractLastCompleteObject(buffer);
      if (
        lastObjects &&
        lastObjects.length !== lastCompleteObjects?.length &&
        !lastCompleteObjects.some(
          (value1) => lastObjects[lastObjects.length - 1]?.link === value1.link
        )
      ) {
        lastCompleteObjects = lastObjects;
        console.log("lastCompleteObject", lastCompleteObjects);
        const parsedMetadata = await parseMetadata([
          lastCompleteObjects[lastCompleteObjects.length - 1],
        ]);
        setChatData((prevChatData) => {
          const newChatData: ChatData = JSON.parse(
            JSON.stringify(prevChatData)
          );
          const selectedChat = newChatData.chats.find(
            (chat) => chat.id === newChatData.selected
          );
          if (selectedChat) {
            const lastMessageIndex = selectedChat.messages.length - 1;
            if (lastMessageIndex >= 0) {
              selectedChat.messages[lastMessageIndex].topSources.push(
                parsedMetadata
              );
            }
          }
          return newChatData;
        });
      }
    }
  };

  const stopApis = () => {
    if (isRecordingInProgress) stopRecording();
    if (loading) {
      dataStreamController.current?.abort();
      metaDataController.current?.abort();
    }
    if (transcripting) transcriptDataController.current?.abort();
  };

  const switchChat = (id: number) => {
    if (isRecordingInProgress) stopRecording();
    stopApis();
    setGreet(true);
    setShowScrollButton(false);
    setChatData((prevChatData) => {
      const newChatData: ChatData = JSON.parse(JSON.stringify(prevChatData));
      const selectedChat = newChatData.chats.find((chat) => chat.id === id);

      if (!selectedChat) {
        newChatData.chats.push({
          id,
          createdAt: new Date().toISOString(),
          messages: [],
        });
      }

      newChatData.selected = id;
      return newChatData;
    });
  };

  const startNewChat = () => {
    if (isRecordingInProgress) stopRecording();
    stopApis();
    setGreet(true);
    setShowScrollButton(false);
    avatarComponentRef.current?.stopAvatarState();
    setAvatarState(AvatarState.IDLE);

    if (chatMode === ChatMode.LIVE_CHAT || chatMode === ChatMode.VIDEO) {
      setLoadingMessage("");
      setDataStreamLoading(false);
      setChatData((prevChatData) => {
        const newChatData: ChatData = JSON.parse(JSON.stringify(prevChatData));
        const selectedChat = newChatData.chats.find(
          (chat) => chat.id === newChatData.selected
        );
        if (selectedChat) {
          selectedChat.messages = [];
        }
        return newChatData;
      });
    }

    setChatData((prevChatData) => {
      const newChatData: ChatData = JSON.parse(JSON.stringify(prevChatData));
      newChatData.chats.push({
        id: newChatData.chats.length + 1,
        createdAt: new Date().toISOString(),
        messages: [],
      });
      newChatData.selected = newChatData.chats.length;
      return newChatData;
    });
  };

  useEffect(() => {
    console.log("recordingTime", recordingTime);
    if (recordingTime >= 240000) {
      stopRecording();
    }
  }, [recordingTime]);

  useEffect(() => {
    console.log("recordedBlob", recordedBlob);

    const transcriptVoice = async () => {
      if (!recordedBlob) return;
      setTranscripting(true);
      const mWavBlob = await getWaveBlob(recordedBlob, false);
      transcriptDataController.current = new AbortController();
      const formData = new FormData();
      formData.append(
        "file",
        mWavBlob,
        `${new Date().toISOString().replace(/[:.]/g, "-")}.wav`
      );
      formData.append("deviceId", deviceId);
      formData.append(
        "tokenId",
        selectedChat?.tokenId ?? deviceId + "-" + Date.now()
      );
      try {
        const textResponse = await fetch(
          `${process.env.REACT_APP_BASE_URL}/uploadAudio`,
          {
            signal: transcriptDataController.current?.signal,
            method: "POST",
            body: formData,
          }
        );
        const textJson = await textResponse.json();
        clearCanvas();
        submitPrompt(textJson.transcription_text);
        scrollToBottom();
      } catch (error) {
      } finally {
        setTranscripting(false);
      }
    };
    if (recordedBlob && !isRecordingInProgress) {
      transcriptVoice();
    }
  }, [isRecordingInProgress, recordedBlob]);

  const handleScroll = () => {
    const scrollable = scrollableRef.current;
    if (!scrollable) return;

    const isAtBottom =
      scrollable.scrollHeight - scrollable.scrollTop ===
      scrollable.clientHeight;
    setShowScrollButton(!isAtBottom);
  };

  const scrollToBottom = () => {
    scrollableRef.current?.scrollTo({
      top: scrollableRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const scrollable = scrollableRef.current;
    scrollable?.addEventListener("scroll", handleScroll);

    return () => {
      scrollable?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    console.log("chatMode", chatMode, avatarState);
  }, [chatMode, avatarState]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/pitch-text" element={<PitchFabriceTextView />} />
        <Route path="/pitch-voice" element={<PitchFabriceVoiceView />} />
        <Route
          path="*"
          element={
            <div
              className="flex flex-col divide-y divide-gray-400"
              style={{
                height: windowHeight,
              }}
            >
              <div className="flex-grow overflow-y-hidden">
                <div className="flex h-full w-full">
                  <main className="flex flex-col relative overflow-y-auto w-full">
                    {chatMode === ChatMode.DELPHI ? (
                      <div
                        id="delphi-container"
                        className="delphi-container"
                        style={{ width: "100%", height: "100vh" }}
                      >
                        {loadDelphiAudioInteraction()}
                      </div>
                    ) : (
                      <>
                        {chatMode === ChatMode.TEXT ||
                        chatMode === ChatMode.PITCH_FABRICE ? (
                          <div
                            ref={scrollableRef}
                            className="flex flex-col flex-grow relative scroll-smooth overflow-y-scroll pb-12 lg:px-5"
                          >
                            {(selectedChat?.messages.length ?? 0) > 0 ? (
                              <>
                                {selectedChat?.messages.map(
                                  (message, index) => (
                                    <article key={index} className="py-2">
                                      <div>
                                        <img
                                          className="size-6 float-left me-2"
                                          src={
                                            message.byUser ? userChat : aiChat
                                          }
                                          alt={
                                            message.byUser
                                              ? "userChat"
                                              : "aiChat"
                                          }
                                        />
                                        <div className="clear-right">
                                          {message.byUser ||
                                          message.message.length > 0 ? (
                                            <Markdown
                                              components={{
                                                p: ({ node, ...props }) => (
                                                  <p
                                                    {...props}
                                                    style={{
                                                      marginBottom: "1em",
                                                    }}
                                                  />
                                                ),
                                              }}
                                            >
                                              {message.message.replace(
                                                "NO_RELEVANT_DATA",
                                                ""
                                              )}
                                            </Markdown>
                                          ) : (
                                            <div className="flex items-center space-x-1">
                                              <div className="size-3 bg-gray-900 rounded-full animate-bounce"></div>
                                              <div className="size-3 bg-gray-900 rounded-full animate-bounce200"></div>
                                              <div className="size-3 bg-gray-900 rounded-full animate-bounce400"></div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {chatMode !== ChatMode.PITCH_FABRICE &&
                                        !message.byUser && (
                                          <>
                                            {message.topSources.length > 0 &&
                                              !message.message?.includes(
                                                "NO_RELEVANT_DATA"
                                              ) && (
                                                <div>
                                                  <p className="text-xl text-gray-700 font-medium my-3">
                                                    Top Sources
                                                  </p>
                                                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-5">
                                                    {message.topSources?.map(
                                                      (source, index) => (
                                                        <div
                                                          key={`source-${index}`}
                                                          className="grid-cols-subgrid"
                                                        >
                                                          <a
                                                            className="link"
                                                            href={source.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                          >
                                                            <LazyLoadImage
                                                              className="w-full h-24 object-cover rounded-md mb-2"
                                                              src={source.img}
                                                              alt={source.title}
                                                            />
                                                            {source.title}
                                                          </a>
                                                        </div>
                                                      )
                                                    )}
                                                    {loading &&
                                                      index ===
                                                        selectedChat.messages
                                                          .length -
                                                          1 && (
                                                        <div className="animate-pulse grid-cols-subgrid gap-2 grid">
                                                          <div className="h-24 bg-slate-200 rounded-md"></div>
                                                          <div className="h-4 bg-slate-200 rounded-md"></div>
                                                        </div>
                                                      )}
                                                  </div>
                                                </div>
                                              )}
                                            {message.status !== "complete" &&
                                              !loading && (
                                                <button
                                                  className={`cursor-pointer flex align-center gap-2 ml-auto w-fit text-lg
                         ${
                           message.status === "error"
                             ? "bg-red-300 text-red-700 border border-red-600"
                             : "bg-gray-200 text-gray-500 border border-gray-500"
                         } my-3 p-2 rounded-lg`}
                                                  onClick={() => {
                                                    const lastMessageIndex =
                                                      selectedChat.messages
                                                        .length - 1;
                                                    setChatData(
                                                      (prevChatData) => {
                                                        const newChatData: ChatData =
                                                          JSON.parse(
                                                            JSON.stringify(
                                                              prevChatData
                                                            )
                                                          );
                                                        const selectedChat =
                                                          newChatData.chats.find(
                                                            (chat) =>
                                                              chat.id ===
                                                              newChatData.selected
                                                          );
                                                        if (selectedChat) {
                                                          if (
                                                            lastMessageIndex >=
                                                            0
                                                          ) {
                                                            selectedChat.messages[
                                                              lastMessageIndex
                                                            ].message = "";
                                                            selectedChat.messages[
                                                              lastMessageIndex
                                                            ].topSources = [];
                                                            selectedChat.messages[
                                                              lastMessageIndex
                                                            ].status =
                                                              "incomplete";
                                                          }
                                                        }
                                                        return newChatData;
                                                      }
                                                    );
                                                    fetchApis(
                                                      selectedChat.messages[
                                                        lastMessageIndex - 1
                                                      ].message
                                                    );
                                                  }}
                                                >
                                                  {message.status === "error"
                                                    ? "Oops, something went wrong. Please try again."
                                                    : "Continue generating"}
                                                  <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth={2}
                                                    stroke="currentColor"
                                                    className="size-6"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                                                    />
                                                  </svg>
                                                </button>
                                              )}
                                          </>
                                        )}
                                    </article>
                                  )
                                )}
                              </>
                            ) : (
                              <div className="grid gap-5 m-auto grid-cols-2 sm:grid-cols-[repeat(auto-fill,_250px)] md:grid-cols-[repeat(2,_250px)] pb-8">
                                {(chatMode === ChatMode.PITCH_FABRICE
                                  ? [
                                      "What are fair Seed & Series A valuations?",
                                      "What are the biggest mistakes marketplace founders make?",
                                    ]
                                  : questions
                                ).map((question, index) => (
                                  <div
                                    key={index}
                                    className="cursor-pointer p-3 min-h-[80px] border text-slate-600 border-slate-400 rounded-xl shadow-md"
                                    onClick={() => submitPrompt(question)}
                                  >
                                    {question}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex-grow">
                            <AvatarComponent
                              ref={avatarComponentRef}
                              index={0}
                              contentLoading={dataStreamLoading}
                              isLastIndex={true}
                              chatMode={chatMode}
                              message={""}
                              loadingMessage={loadingMessage.replace(
                                "NO_RELEVANT_DATA",
                                ""
                              )}
                              clearLoadingMessage={() => {
                                setLoadingMessage("");
                              }}
                              updateUserMessage={submitPrompt}
                              greet={greet}
                              setGreet={setGreet}
                              avatarState={avatarState}
                              setAvatarState={setAvatarState}
                              onUpdateAvatarStatus={setAvatarStatus}
                              language={selectedLanguage}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div className="flex flex-col items-center w-full absolute bottom-36 sm:bottom-32 md:bottom-28 left-1/2 transform -translate-x-1/2 gap-2">
                      {showScrollButton && (
                        <img
                          className="size-8 cursor-pointer"
                          src={scrollDown}
                          alt="scrollDown"
                          onClick={scrollToBottom}
                        />
                      )}
                      <div
                        className={`md:w-full grid gap-4 grid-flow-row md:grid-flow-col ${
                          chatMode === ChatMode.LIVE_CHAT
                            ? "place-content-between"
                            : "place-content-center"
                        } lg:px-5 items-center`}
                      >
                        {chatMode === ChatMode.LIVE_CHAT && (
                          <div className="order-2 md:order-1">
                            <LanguageSelection
                              selectedLanguage={selectedLanguage}
                              setSelectedLanguage={(value) => {
                                setSelectedLanguage(value);
                                setGreet(true);
                              }}
                            />
                          </div>
                        )}
                        <div className="order-1 md:order-2 col-span-2 md:col-span-1">
                          <ChatModeSwitch
                            chatMode={chatMode}
                            setChatMode={(chatMode) => {
                              setChatMode(chatMode);
                              setShowScrollButton(false);
                            }}
                          />
                        </div>
                        {(chatMode === ChatMode.VIDEO ||
                          (chatMode === ChatMode.LIVE_CHAT &&
                            avatarStatus === AvatarStatus.INITIALIZED)) && (
                          <div className="order-2 md:order-3 text-end">
                            <button
                              onClick={() =>
                                avatarComponentRef.current?.stopAvatarState()
                              }
                              className="flex bg-black text-white px-2 gap-2 py-2 rounded-md"
                            >
                              {avatarStatus === AvatarStatus.INITIALIZED && (
                                <>
                                  {dataStreamLoading &&
                                  loadingMessage.split(" ").length < 15 ? (
                                    <img
                                      className="size-6"
                                      src={processing}
                                      alt="processing"
                                    />
                                  ) : avatarState === AvatarState.SPEAKING ? (
                                    <img
                                      className="size-6"
                                      src={speaking}
                                      alt="speaking"
                                    />
                                  ) : (
                                    avatarState === AvatarState.LISTENING && (
                                      <img
                                        className="size-6"
                                        src={listening}
                                        alt="listening"
                                      />
                                    )
                                  )}
                                </>
                              )}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="size-6 rounded-full border border-white p-1"
                              >
                                {avatarState === AvatarState.IDLE ? (
                                  <path
                                    fillRule="evenodd"
                                    d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                                    clipRule="evenodd"
                                  />
                                ) : (
                                  <path
                                    fillRule="evenodd"
                                    d="M4.5 7.5a3 3 0 0 1 3-3h9a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9Z"
                                    clipRule="evenodd"
                                  />
                                )}
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {chatMode === ChatMode.TEXT && (
                      <div className="lg:px-5 pb-2 gap-2">
                        {isRecordingInProgress || transcripting ? (
                          <div className="border border-slate-400 rounded-xl">
                            {isRecordingInProgress && (
                              <div className="p-2 flex items-center gap-2">
                                <img
                                  width={24}
                                  height={24}
                                  src={closeCircle}
                                  alt="check"
                                  onClick={() => {
                                    if (isRecordingInProgress) stopRecording();
                                    clearCanvas();
                                  }}
                                />
                                <VoiceVisualizer
                                  mainContainerClassName="flex-grow"
                                  height={56}
                                  controls={recorderControls}
                                  isControlPanelShown={false}
                                  mainBarColor={"#2f2f2f"}
                                />
                                <img
                                  width={24}
                                  height={24}
                                  src={checkCircle}
                                  alt="check"
                                  onClick={() => {
                                    stopRecording();
                                  }}
                                />
                              </div>
                            )}
                            {transcripting && (
                              <div className="p-3 text-center">
                                <img
                                  className="w-4/5 h-12 mx-auto"
                                  src={audioWave}
                                  alt="transcripting"
                                />
                                <p>Transcribing...</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <form
                              className="p-2 flex border border-slate-400 rounded-xl gap-2 items-center"
                              onSubmit={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (prompt.length > 0 && !loading) {
                                  submitPrompt(prompt);
                                  setPrompt("");
                                }
                              }}
                            >
                              <textarea
                                className="p-1 flex-grow form-textarea border-none outline-none focus:ring-0 resize-none"
                                placeholder="Enter a prompt"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === "Enter" &&
                                    !event.shiftKey
                                  ) {
                                    event.preventDefault();
                                    if (prompt.length > 0 && !loading) {
                                      submitPrompt(prompt);
                                      setPrompt("");
                                    }
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => startRecording()}
                                className="p-1"
                              >
                                <img src={mic} alt="mic" />
                              </button>
                              <div className="h-6 w-[1px] bg-slate-500 mx-1" />
                              <button
                                type="submit"
                                disabled={
                                  prompt.length === 0 ||
                                  loading ||
                                  selectedChat?.messages.at(
                                    selectedChat?.messages?.length - 1
                                  )?.message?.length === 0
                                }
                              >
                                <img
                                  src={
                                    prompt.length === 0 ||
                                    loading ||
                                    selectedChat?.messages.at(
                                      selectedChat?.messages?.length - 1
                                    )?.message?.length === 0
                                      ? sendDisabled
                                      : send
                                  }
                                  alt="send"
                                />
                              </button>
                            </form>
                            <small>
                              Fabrice AI is an experimental tool and may be
                              inaccurate at times. Read{" "}
                              <a
                                className="underline text-blue-700"
                                href={`${process.env.REACT_APP_DISCLAIMER_URL}`}
                                target={"_blank"}
                                rel="noreferrer"
                              >
                                Disclaimers
                              </a>
                            </small>
                          </>
                        )}
                      </div>
                    )}
                  </main>
                </div>
              </div>
              <ToastContainer />
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
